import {
  CACHE_HEADER_ENABLED,
  CACHE_HEADER_STALE_TIME,
} from "@sinch/cache-helpers";
import { z } from "zod";
import {
  dashboardMetadataSchema,
  metadataToObject,
} from "../../portals/dashboard/utils/metadata";
import type { ApiStrategy, ShellUser } from "../types";
import { dashboardFetch } from "./dashboardFetch";

const dashboardUserSchema = z.object({
  username: z.string(),
  id: z.number(),
  activeProject: z.string(),
  createdAt: z.string(),
  firstName: z.string(),
  lastName: z.string(),
  trackingId: z.string(),
  hasSsoSession: z.boolean(),
  userMetadata: dashboardMetadataSchema,
  settings: z
    .object({
      defaultAccountId: z.string(),
      userCountryCode: z.string(),
    })
    .partial(),
});

const accountMetadataSchema = z
  .object({
    userId: z.number().nullable().optional(),
    isUpdatable: z.boolean().optional(),
    isDeletable: z.boolean().optional(),
    key: z.string().optional(),
    value: z.string().optional(),
    type: z.string().optional(),
  })
  .deepPartial();

const accountManagerSchema = z
  .object({
    email: z.string().optional(),
    name: z.string().optional(),
  })
  .deepPartial();

const accountRoleSchema = z
  .object({
    name: z.string().optional(),
    permissionsRaw: z.array(z.string()).optional(),
  })
  .deepPartial();

const dashboardAccountSchema = z
  .object({
    id: z.string().optional(),
    legacyId: z.string().optional(),
    name: z.string().optional(),
    primaryEmail: z.string().optional(),
    phoneNumber: z.string().optional(),
    companyName: z.string().optional(),
    address1: z.string().optional(),
    address2: z.string().optional(),
    city: z.string().optional(),
    state: z.string().optional(),
    zipCode: z.string().optional(),
    createdAt: z.string().optional(),
    currency: z.string().optional(),
    vat: z.string().optional(),
    accountStatus: z.string().optional(),
    accountType: z.string().optional(),
    paymentTerms: z.string().optional(),
    countryCode: z.string().optional(),
    accountManager: accountManagerSchema.optional(),
    projectsIntegrated: z.array(z.string()).optional(),
    showMigrationPopup: z.boolean().optional(),
    migratedFrom: z.string().optional(),
    accountMetadata: z.array(accountMetadataSchema).optional(),
    defaultProjectId: z.string().optional(),
    blockStatus: z.string().optional(),
    parameters: z.record(z.unknown()).optional(),
    meRole: accountRoleSchema.optional(),
    rolesTotalItems: z.number().optional(),
    mfaEnabled: z.boolean().optional(),
  })
  .deepPartial();

export type DashboardUser = z.infer<typeof dashboardUserSchema>;
export type DashboardAccount = z.infer<typeof dashboardAccountSchema>;

export const dashboardFetchUserURL = `${window.__ENV__VARIABLES?.K8S_BACKEND_URL}/api/v1/me`;
const dashboardFetchProjectURL = `${window.__ENV__VARIABLES?.K8S_BACKEND_URL}/api/v1/project`;
const dashboardFetchAccountURL = `${window.__ENV__VARIABLES?.K8S_BACKEND_URL}/api/v1/account`;

export const getDashboardUser: ApiStrategy["getUser"] = (
  _token,
  requestInit
) => {
  const userRequest = dashboardFetch(dashboardFetchUserURL, {
    method: "GET",
    headers: {
      [CACHE_HEADER_ENABLED]: "true",
      [CACHE_HEADER_STALE_TIME]: "10000",
      ...requestInit?.headers,
    },
    ...requestInit,
  });
  const projectRequest = dashboardFetch(dashboardFetchProjectURL, {
    method: "GET",
    headers: {
      [CACHE_HEADER_ENABLED]: "true",
      [CACHE_HEADER_STALE_TIME]: "10000",
      ...requestInit?.headers,
    },
    ...requestInit,
  });
  const accountRequest = dashboardFetch(dashboardFetchAccountURL, {
    method: "GET",
    headers: {
      [CACHE_HEADER_ENABLED]: "true",
      [CACHE_HEADER_STALE_TIME]: "10000",
      ...requestInit?.headers,
    },
    ...requestInit,
  });

  return Promise.all([
    userRequest.then((res) => res.json()),
    projectRequest
      .then((res) => res.json())
      .catch((e) => (console.error(e), {})),
    accountRequest
      .then((res) => res.json())
      .catch((e) => (console.error(e), {})),
  ]).then(([userData, projectData, accountData]) =>
    toShellUser({
      ...userData,
      activeProject: projectData?.projectId,
      accountDetails: accountData,
    })
  );
};

export const toShellUser = (data: unknown): ShellUser => {
  const parsed = dashboardUserSchema
    .extend({
      accountDetails: dashboardAccountSchema,
    })
    .safeParse(data);

  if (!parsed.success) console.error("Invalid response from /me", parsed.error);

  const { accountDetails: dashboardAccount, ...dashboardUser } = parsed.success
    ? parsed.data
    : (data as Partial<DashboardUser> & {
        accountDetails?: Partial<DashboardAccount>;
      });
  const userRole = dashboardAccount?.meRole?.name;
  return {
    id: String(dashboardUser?.id) || "",
    name: dashboardUser?.firstName + " " + dashboardUser?.lastName,
    // Email is used as username in dashboard
    email: dashboardUser?.username || "",
    rights: [],
    language: undefined,
    profile_image: undefined,
    roles: userRole ? [userRole] : [],
    ...(dashboardUser?.settings?.defaultAccountId && {
      account: {
        id: dashboardUser?.settings.defaultAccountId,
        country:
          dashboardAccount?.countryCode ??
          dashboardUser?.settings.userCountryCode,
        createdAt: dashboardAccount?.createdAt,
      },
    }),
    status: undefined,
    activeProject: dashboardUser?.activeProject,
    createdAt: dashboardUser.createdAt,
    settings: {
      accountStatus: dashboardAccount?.accountStatus,
      accountPaymentTerms: dashboardAccount?.paymentTerms?.replaceAll("_", ""),
      accountCurrency: dashboardAccount?.currency,
      accountType: dashboardAccount?.accountType,
      accountHas2FAEnabled: dashboardAccount?.mfaEnabled,
      hasSsoSession: dashboardUser?.hasSsoSession,
      userTrackingId: dashboardUser?.trackingId,
      userMetadata: dashboardUser?.userMetadata,
      firstName: dashboardUser?.firstName,
      lastName: dashboardUser?.lastName,
      userHasPhoneInProfile: !!dashboardAccount?.phoneNumber,
      ...metadataToObject(dashboardUser?.userMetadata ?? []),
    },
  };
};
